import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const priorityColors = {
  low: { bg: 'var(--gray-100)', text: 'var(--gray-600)', border: 'var(--gray-300)' },
  medium: { bg: 'var(--blue-50)', text: 'var(--blue-600)', border: 'var(--blue-300)' },
  high: { bg: 'var(--orange-50)', text: 'var(--orange-600)', border: 'var(--orange-300)' },
  urgent: { bg: 'var(--red-50)', text: 'var(--red-600)', border: 'var(--red-300)' }
};

const categoryIcons = {
  content: '📝',
  design: '🎨',
  development: '💻',
  marketing: '📈',
  research: '🔍',
  planning: '📋',
  general: '📌'
};

const TaskPreviewCard = ({
  task,
  onSave,
  onEdit,
  onDelete,
  isEditing = false,
  compact = false
}) => {
  const [expanded, setExpanded] = useState(!compact);
  const [editedTask, setEditedTask] = useState(task);
  const [saving, setSaving] = useState(false);

  const colors = priorityColors[task.priority] || priorityColors.medium;
  const categoryIcon = categoryIcons[task.category] || categoryIcons.general;

  const handleSave = async () => {
    if (onSave) {
      setSaving(true);
      await onSave(editedTask);
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedTask(prev => ({ ...prev, [field]: value }));
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      border: `1px solid ${colors.border}`,
      borderRadius: '0.75rem',
      padding: compact ? '0.75rem' : '1rem',
      marginBottom: '0.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s'
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '0.75rem'
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flex: 1
    },
    categoryIcon: {
      fontSize: '1.25rem'
    },
    title: {
      fontSize: compact ? '0.875rem' : '1rem',
      fontWeight: '600',
      color: 'var(--gray-900)',
      margin: 0,
      flex: 1
    },
    titleInput: {
      fontSize: compact ? '0.875rem' : '1rem',
      fontWeight: '600',
      color: 'var(--gray-900)',
      border: '1px solid var(--gray-300)',
      borderRadius: '0.375rem',
      padding: '0.375rem 0.5rem',
      flex: 1,
      width: '100%'
    },
    badges: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap'
    },
    priorityBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.125rem 0.5rem',
      fontSize: '0.6875rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      borderRadius: '9999px',
      backgroundColor: colors.bg,
      color: colors.text
    },
    hoursBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.125rem 0.5rem',
      fontSize: '0.6875rem',
      fontWeight: '500',
      borderRadius: '9999px',
      backgroundColor: 'var(--gray-100)',
      color: 'var(--gray-600)'
    },
    expandButton: {
      background: 'none',
      border: 'none',
      padding: '0.25rem',
      cursor: 'pointer',
      color: 'var(--gray-400)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '0.25rem'
    },
    description: {
      marginTop: '0.75rem',
      fontSize: '0.875rem',
      color: 'var(--gray-600)',
      lineHeight: '1.5'
    },
    descriptionInput: {
      width: '100%',
      marginTop: '0.75rem',
      fontSize: '0.875rem',
      color: 'var(--gray-600)',
      lineHeight: '1.5',
      border: '1px solid var(--gray-300)',
      borderRadius: '0.375rem',
      padding: '0.5rem',
      minHeight: '80px',
      resize: 'vertical'
    },
    meta: {
      marginTop: '0.75rem',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.75rem',
      fontSize: '0.75rem',
      color: 'var(--gray-500)'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.25rem',
      marginTop: '0.5rem'
    },
    tag: {
      padding: '0.125rem 0.375rem',
      fontSize: '0.6875rem',
      backgroundColor: 'var(--primary-50)',
      color: 'var(--primary-700)',
      borderRadius: '0.25rem'
    },
    actions: {
      marginTop: '0.75rem',
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'flex-end'
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.375rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: 'var(--primary-600)',
      color: 'white',
      border: 'none'
    },
    secondaryButton: {
      backgroundColor: 'white',
      color: 'var(--gray-700)',
      border: '1px solid var(--gray-300)'
    },
    dangerButton: {
      backgroundColor: 'white',
      color: 'var(--red-600)',
      border: '1px solid var(--red-300)'
    },
    editFields: {
      marginTop: '0.75rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '0.75rem'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    fieldLabel: {
      fontSize: '0.6875rem',
      fontWeight: '600',
      color: 'var(--gray-500)',
      textTransform: 'uppercase'
    },
    fieldInput: {
      padding: '0.375rem 0.5rem',
      fontSize: '0.8125rem',
      border: '1px solid var(--gray-300)',
      borderRadius: '0.375rem',
      color: 'var(--gray-700)'
    },
    select: {
      padding: '0.375rem 0.5rem',
      fontSize: '0.8125rem',
      border: '1px solid var(--gray-300)',
      borderRadius: '0.375rem',
      color: 'var(--gray-700)',
      backgroundColor: 'white'
    }
  };

  const renderViewMode = () => (
    <>
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <span style={styles.categoryIcon}>{categoryIcon}</span>
          <h4 style={styles.title}>{task.title}</h4>
        </div>
        <div style={styles.badges}>
          <span style={styles.priorityBadge}>
            {task.priority === 'urgent' && <ExclamationCircleIcon style={{ width: '0.75rem', height: '0.75rem' }} />}
            {task.priority}
          </span>
          {task.estimated_hours && (
            <span style={styles.hoursBadge}>
              <ClockIcon style={{ width: '0.75rem', height: '0.75rem' }} />
              {task.estimated_hours}h
            </span>
          )}
          {compact && (
            <button
              style={styles.expandButton}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <ChevronUpIcon style={{ width: '1rem', height: '1rem' }} />
              ) : (
                <ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />
              )}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <>
          {task.description && (
            <p style={styles.description}>{task.description}</p>
          )}

          <div style={styles.meta}>
            <span style={styles.metaItem}>
              <span style={{ fontWeight: '500' }}>Category:</span> {task.category}
            </span>
            {task.due_date_offset_days && (
              <span style={styles.metaItem}>
                <span style={{ fontWeight: '500' }}>Due:</span> {task.due_date_offset_days} days
              </span>
            )}
            {task.suggested_assignee && (
              <span style={styles.metaItem}>
                <span style={{ fontWeight: '500' }}>Assignee:</span> {task.suggested_assignee}
              </span>
            )}
          </div>

          {task.tags && task.tags.length > 0 && (
            <div style={styles.tags}>
              {task.tags.map((tag, index) => (
                <span key={index} style={styles.tag}>{tag}</span>
              ))}
            </div>
          )}

          {(onSave || onEdit || onDelete) && (
            <div style={styles.actions}>
              {onDelete && (
                <button
                  style={{ ...styles.actionButton, ...styles.dangerButton }}
                  onClick={() => onDelete(task)}
                >
                  <TrashIcon style={{ width: '0.875rem', height: '0.875rem' }} />
                  Remove
                </button>
              )}
              {onEdit && (
                <button
                  style={{ ...styles.actionButton, ...styles.secondaryButton }}
                  onClick={() => onEdit(task)}
                >
                  <PencilIcon style={{ width: '0.875rem', height: '0.875rem' }} />
                  Edit
                </button>
              )}
              {onSave && (
                <button
                  style={{ ...styles.actionButton, ...styles.primaryButton }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    'Saving...'
                  ) : (
                    <>
                      <PlusIcon style={{ width: '0.875rem', height: '0.875rem' }} />
                      Create Task
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  const renderEditMode = () => (
    <>
      <div style={styles.header}>
        <input
          type="text"
          style={styles.titleInput}
          value={editedTask.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          placeholder="Task title"
        />
      </div>

      <textarea
        style={styles.descriptionInput}
        value={editedTask.description}
        onChange={(e) => handleFieldChange('description', e.target.value)}
        placeholder="Task description..."
      />

      <div style={styles.editFields}>
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Priority</label>
          <select
            style={styles.select}
            value={editedTask.priority}
            onChange={(e) => handleFieldChange('priority', e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Category</label>
          <select
            style={styles.select}
            value={editedTask.category}
            onChange={(e) => handleFieldChange('category', e.target.value)}
          >
            <option value="content">Content</option>
            <option value="design">Design</option>
            <option value="development">Development</option>
            <option value="marketing">Marketing</option>
            <option value="research">Research</option>
            <option value="planning">Planning</option>
            <option value="general">General</option>
          </select>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Estimated Hours</label>
          <input
            type="number"
            style={styles.fieldInput}
            value={editedTask.estimated_hours}
            onChange={(e) => handleFieldChange('estimated_hours', parseFloat(e.target.value))}
            min="0.5"
            step="0.5"
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Due (days from now)</label>
          <input
            type="number"
            style={styles.fieldInput}
            value={editedTask.due_date_offset_days || ''}
            onChange={(e) => handleFieldChange('due_date_offset_days', e.target.value ? parseInt(e.target.value) : null)}
            min="1"
          />
        </div>
      </div>

      <div style={styles.actions}>
        {onDelete && (
          <button
            style={{ ...styles.actionButton, ...styles.dangerButton }}
            onClick={() => onDelete(editedTask)}
          >
            Cancel
          </button>
        )}
        <button
          style={{ ...styles.actionButton, ...styles.primaryButton }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Task'}
        </button>
      </div>
    </>
  );

  return (
    <div
      style={styles.card}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
      }}
    >
      {isEditing ? renderEditMode() : renderViewMode()}
    </div>
  );
};

export default TaskPreviewCard;
